import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProfilePictureUpload } from "@/components/ProfilePictureUpload";
import { hasMemberPhoto } from "@/lib/memberPhoto";

type Ctx = { ensurePhoto: () => Promise<boolean> };
const PhotoGateContext = createContext<Ctx>({ ensurePhoto: async () => true });

/** Going, organising and joining meet-ups need a member photo so others can recognise you on the day. */
export const PhotoGateProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const ensurePhoto = useCallback(async () => {
    if (!user) return true; // signed-out flows show their own sign-in prompt
    const { data } = await supabase.from("profiles").select("avatar_url, first_name").eq("id", user.id).maybeSingle();
    if (hasMemberPhoto(data?.avatar_url)) return true;
    setFirstName(data?.first_name || "");
    setOpen(true);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, [user]);

  const finish = (ok: boolean) => {
    setOpen(false);
    resolver.current?.(ok);
    resolver.current = null;
  };

  return (
    <PhotoGateContext.Provider value={{ ensurePhoto }}>
      {children}
      <Dialog open={open} onOpenChange={(o) => !o && finish(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add a photo so others can recognise you</DialogTitle>
            <DialogDescription>
              Parents and carers meeting you on the day need to know who to look for. Add a clear photo of your face to continue.
            </DialogDescription>
          </DialogHeader>
          {user && (
            <ProfilePictureUpload
              inline
              userId={user.id}
              currentAvatarUrl={null}
              firstName={firstName || "You"}
              onAvatarUpdate={(url) => {
                void qc.invalidateQueries({ queryKey: ["user-profile", user.id] });
                if (url) finish(true);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </PhotoGateContext.Provider>
  );
};

export const usePhotoGate = () => useContext(PhotoGateContext);
